<template>
    <div
      class="finantial-settings-accounts-page"
      v-loading="loadingConnectedAccount"
    >
        <!-- Subtitle Connected Account  -->
        <h2
          v-if="connectedAccount"
          class="app-content-subtitle finantial-settings-accounts-page__title"
        >
          <app-i18n code="subpropertySettings.finantial.accounts.subtitle.connectedAccount" />  
        </h2>
        
        <!-- Connected account card -->
        <finantial-settings-account-card
          v-if="connectedAccount" 
          :requirements-hash="connectedAccount.requirements"
          :payments-enabled="paymentsEnabled"
          :payouts-enabled="payoutsEnabled"
          :image-url="connectedAccountCard.imageUrl"          
          :title="connectedAccountCard.title"
          :tooltip-caption="connectedAccountCard.tooltipCaption"
          :tooltip-email="connectedAccountCard.tooltipEmail"
          :on-click-edit-fn="connectedAccountCard.onClickEditFn"
          :on-click-destroy-fn="connectedAccountCard.onClickDestroyFn"        
        />
      
        <!-- Empty connected account -->
        <app-empty-page
          v-else
          :icon-class="connectedAccountEmpty.iconClass"
          :empty-text="connectedAccountEmpty.emptyText"
          :text-button="connectedAccountEmpty.textButton"
          :on-click-button-fn="connectedAccountEmpty.onClickButtonFn"
        />
        
        <!-- Subtitle Additional Questions -->
        <template v-if="accountQuestionsRequirements.length">
          <h2 class="app-content-subtitle finantial-settings-accounts-page__title">
            <app-i18n code="subpropertySettings.finantial.accounts.subtitle.additionalQuestions" />
          </h2>

          <finantial-settings-account-question
            v-for="question in accountQuestionsRequirements"
            :key="`finantial-settings-account-question--${question}`"
            :code="question"
            :connected-account-name="connectedAccount.companyName"
            @add="onQuestionAdd"
            @nope="onQuestionNope"
          />
        </template>
        
        <!-- Subtitle Connected perssons -->
        <h2
          v-if="connectedAccountId"
          class="app-content-subtitle finantial-settings-accounts-page__title"
        >
          <app-i18n code="subpropertySettings.finantial.accounts.subtitle.connectedPersons" />
        </h2>

        <template v-if="connectedAccountId">
          <!-- Person list -->
          <app-stripe-person-list-table
            v-if="!showEmptyConnectedPerson"
            :propertyId="propertyId"
            :connectedAccountId="connectedAccountId"
          />

          <!-- Connected Person Empty -->
          <app-empty-page
            v-else
            :icon-class="connectedPersonEmpty.iconClass"
            :empty-text="connectedPersonEmpty.emptyText"
            :text-button="connectedPersonEmpty.textButton"
            :on-click-button-fn="connectedPersonEmpty.onClickButtonFn"
          />
        </template>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { get } from 'lodash';

import StripeFrontend from 'shared-stripe-services/dist/Frontend';

import { i18n } from '@shared/i18n';
import config from '@/config';

import FinantialSettingsAccountCard from '@modules/subproperty-settings/finantial-settings/finantial-settings-accounts/components/finantial-settings-account-card.vue'
import FinantialSettingsAccountQuestion from '@modules/subproperty-settings/finantial-settings/finantial-settings-accounts/components/finantial-settings-accounts-question.vue'
import StripePersonListTable from '@modules/stripe/stripe-person/components/stripe-person-list-table.vue';

const stripeFrontend = new StripeFrontend({
  publishableKey: config.stripeConfig.publishableKey,
  fileUploadEndpoint: config.stripeConfig.fileUploadEndpoint,
});

export default {
  name: 'finantial-settings-accounts-page',

  props: {
    propertyId: {
      type: String,
      required: true,
    },
    subpropertyId: {
      type: String,
      required: true,
    },
  },

  computed: {
    ...mapGetters({
      connectedPersons: 'stripePerson/list/rows',
      connectedAccount: 'paymentSettings/connectedAccount',
      subpropertyLogoUrl: 'paymentSettings/subpropertyLogoUrl',
      loadingConnectedAccount: 'paymentSettings/loadingConnectedAccount',
      loadingPersons: 'stripePerson/list/loading',
    }),
    connectedAccountId() {
      return get(this, 'connectedAccount.id', null);
    },
    paymentsEnabled() {
      return get(this, 'connectedAccount.chargesEnabled', false);
    },
    payoutsEnabled() {
      return get(this, 'connectedAccount.payoutsEnabled', false);
    },
    connectedAccountCard() {
      return {
        imageUrl: this.subpropertyLogoUrl,
        title: this.connectedAccount.companyName,
        tooltipCaption: i18n('subpropertySettings.finantial.accounts.deleteText', ),
        tooltipEmail: 'support@owqlo.com',
        onClickEditFn: this.onClickEditAccount,
        onClickDestroyFn: () => {},
      }
    },
    connectedAccountEmpty() {
      return {
        iconClass: "icon-home",
        emptyText: i18n('subpropertySettings.finantial.accounts.emptyAccountText'),
        textButton: i18n('subpropertySettings.finantial.accounts.emptyAccountButton'),
        onClickButtonFn: this.onClickEmptyAccount
      }
    },
    connectedPersonEmpty() {
      return {
        iconClass: "icon-home",
        emptyText: i18n('subpropertySettings.finantial.accounts.emptyPersonText'),
        textButton: i18n('subpropertySettings.finantial.accounts.emptyPersonButton'),
        onClickButtonFn: this.onClickEmptyPerson
      }
    },
    isEmptyListPersons() {
      return this.connectedPersons.length === 0;
    },
    showEmptyConnectedPerson() {
      return !this.loadingPersons && this.connectedPersons.length === 0 && this.connectedAccountId;
    },
    accountQuestionsRequirements() {
      if (!this.connectedAccount) return {}
      const requirementsHash = this.connectedAccount.requirements || {}
      const requirements = [...(new Set([
        ...(requirementsHash.pastDue || []),
        ...(requirementsHash.currentlyDue || []),
        ...(requirementsHash.eventuallyDue || []),
      ]))]
      return requirements.filter(r => ['relationship.director', 'relationship.owner'].includes(r))
    },
  },

  methods: {
    ...mapActions({
      doSetupParams: 'stripePerson/list/doSetupParams',
      doFetch: 'stripePerson/list/doFetch',
      doUpdateConnectedAccount: 'stripeConnectedAccount/form/doUpdateProvided',
    }),
    onClickEmptyAccount() {
      this.$router.push({
        name: 'stripeConnectedAccountCreate',
      });
    },
    onClickEmptyPerson() {
      this.$router.push({
        name: 'stripePersonCreate',
        params: {
          connectedAccountId: this.connectedAccountId
        }
      });
    },
    onClickEditAccount() {
      this.$router.push({
        name: 'stripeConnectedAccountEdit',
        params: {
          id: this.connectedAccount.id,
        },
      });
    },
    onQuestionAdd(params) {
      this.$router.push({
        name: 'stripePersonCreate',
        params: {
          ...params,
          connectedAccountId: this.connectedAccountId,
        },
      })
    },
    async onQuestionNope({ isOwner = false, isDirector = false, isExecutive = false } = {}) {
      const { propertyId, connectedAccountId } = this

      const { token: { id: token } } = await stripeFrontend.createCompanyAccountToken({
        ownersProvided: isOwner,
        directorsProvided: isDirector,
        executivesProvided: isExecutive,
      })

      await this.doUpdateConnectedAccount({ propertyId, connectedAccountId, token })

      this.$emit('refresh')
    },
  },
  watch: {
    connectedAccountId: {
      handler(connectedAccountId) {
        if (!connectedAccountId) return;
        const { propertyId } = this;
        this.doSetupParams({ propertyId, connectedAccountId });
        this.doFetch();
      },
      immediate: true,
    },
  },
  components: {
    FinantialSettingsAccountCard,
    FinantialSettingsAccountQuestion,
    [StripePersonListTable.name]: StripePersonListTable
  },
}
</script>

<style lang="scss" scoped>
.finantial-settings-accounts-page {
  &__title {
    margin: 40px 0 8px;
    color: #616161;
    font-size: 18px;
    text-transform: uppercase;

    &:first-of-type {
      margin: 16px 0;
    }
  }
}
</style>